<template>
  <div>
    <Loading v-if="isLoading" loading-message="載入中..." :background-color="'#ffffff'" />

    <div class="page">
      <div v-if="deepGet(form, 'config.content_page.form_title')" class="page__title">
        {{ deepGet(form, 'config.content_page.form_title') }}
      </div>
      <div v-if="deepGet(form, 'config.content_page.form_desc')" class="page__desc">
        {{ deepGet(form, 'config.content_page.form_desc') }}
      </div>

      <!-- Manual Check-in Form as Main Content -->
      <div v-if="showingManualCheckinForm" class="page__content">
        <ManualCheckinForm
          ref="manualCheckinForm"
          @submit="handleManualCheckinSubmit"
          @cancel="handleManualCheckinCancel"
        />
      </div>
      <div v-else>
        <!-- Status Message View -->
        <div class="page__content">
          <CheckinStatusDisplay :message="content" />
        </div>

        <!-- Customer Profile -->
        <CustomerProfile
          :profile="profile"
          :user="user"
        />

        <!-- Manual Check-in Button -->
        <SharedButton
          v-if="showManualCheckinButton"
          variant="s-btn-outline-primary"
          class="mt-4"
          @click="showingManualCheckinForm = true"
        >
          手動報到
        </SharedButton>

        <MemberButton
          :button-style="`s-btn-outline-primary`"
          class="mt-4"
          button-text="回 LINE OA"
          type="OA"
        >
        </MemberButton>
      </div>
    </div>
  </div>
</template>

<script>
import {
  set,
  getHours,
  getMinutes,
  isAfter,
  isBefore,
} from "date-fns";
import formApi from "@/apis/liff/v2/form";
import Loading from "@/components/Page/Liff/Shared/Loading";
import MemberButton from "@/components/Page/Liff/Shared/MemberButton.vue";
import SharedButton from "@/components/Page/Liff/Shared/Button.vue";
import ManualCheckinForm from "@/components/Page/Liff/Form/ManualCheckinForm.vue";
import CheckinStatusDisplay from "@/components/Page/Liff/Form/CheckinStatusDisplay.vue";
import CustomerProfile from "@/components/Page/Liff/Form/CustomerProfile.vue";
import deepGet from "@/utils/deepGet";
import { mapGetters } from "vuex";

export default {
  components: {
    Loading,
    MemberButton,
    SharedButton,
    ManualCheckinForm,
    CheckinStatusDisplay,
    CustomerProfile,
  },
  data() {
    return {
      form: null,
      formResult: null,
      isLoading: true,
      content: '',
      showingManualCheckinForm: false
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    deepGet,
    async init() {
      await this.fetchForm();
      this.checkCheckinStatus();
      this.isLoading = false;
    },
    async fetchForm() {
      try {
        let response = await formApi.getForm(this.$route.params.form_id);
        this.form = response.data.data;

        response = await formApi.checkFormResult(this.$route.params.form_id);
        this.formResult = response.data.data;
      } catch (error) {
        if (error.response.status === 404) {
          this.$swal("錯誤", "找不到此問券", "error");
        } else {
          console.error(error);
          this.$swal("錯誤", "", "error");
        }
      }
    },
    async checkCheckinStatus() {
      if (this.form.type != 'event' || this.form.status == 'draft' || this.form.status == 'closed') {
        this.setStatusView('找不到此活動');
        return;
      }

      const now = new Date();
      const checkinPeriod = this.getCheckinPeriod();

      if (isBefore(now, checkinPeriod.startAt)) {
        this.setStatusView('活動尚未開始');
        return;
      }

      if (isAfter(now, checkinPeriod.endAt)) {
        this.setStatusView('此活動無法報到');
        formApi.setCheckinExpired(this.$route.params.form_id);
        return;
      }

      if (!this.formResult) {
        if (this.canDoManualCheckin) {
          this.showingManualCheckinForm = true;
        } else {
          this.setStatusView('您尚未報名');
        }
        try {
          await formApi.setCheckinSuccess(this.$route.params.form_id);
        } catch (error) {
          console.error(error);
        }
        return;
      }

      const approvedStatus = ['approved', 'auto-approved'];
      if (approvedStatus.includes(this.formResult.status) && this.formResult.checkin_status) {
        this.setStatusView('報到成功');
        return;
      }

      if (this.formResult.status === 'pending') {
        this.setStatusView('報到失敗（報名審核中)');
        return;
      }

      if (this.formResult.status === 'rejected') {
        this.setStatusView('報到失敗（報名不通過)');
        return;
      }

      try {
        await formApi.setCheckinSuccess(this.$route.params.form_id);
        this.setStatusView('報到成功');
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "報到失敗", "error");
      }
    },
    getCheckinPeriod() {
      let startAt = this.form.config.event_setting.checkin_start_at;
      startAt = set(new Date(startAt), {
        hours: String(getHours(new Date(startAt))).padStart(2, "0"),
        minutes: String(getMinutes(new Date(startAt))).padStart(2, "0"),
      });
      let endAt = this.form.config.event_setting.checkin_end_at;
      endAt = set(new Date(endAt), {
        hours: String(getHours(new Date(endAt))).padStart(2, "0"),
        minutes: String(getMinutes(new Date(endAt))).padStart(2, "0"),
      });
      return { startAt, endAt };
    },
    setStatusView(message) {
      this.content = message;
      this.showingManualCheckinForm = false;
    },
    async handleManualCheckinSubmit(formData) {
      try {
        const response = await formApi.manualCheckin(this.$route.params.form_id, formData);

        if (response.data.status === 'success') {
          this.setStatusView('報到成功');
          const resultResponse = await formApi.checkFormResult(this.$route.params.form_id);
          this.formResult = resultResponse.data.data;
        } else {
          this.$swal("錯誤", response.data.message || "無報名資料", "error");
        }
      } catch (error) {
        console.error(error);
        const message = error.response?.data?.message || "無報名資料";
        this.$swal("錯誤", message, "error");
      }
    },
    handleManualCheckinCancel() {
      this.setStatusView('您尚未報名');
    },
    getModuleConfig(key) {
      return this.$store.getters["liffModule/getConfig"]("liff_form", key);
    }
  },
  computed: {
    ...mapGetters("liffAuth", ["user"]),
    profile() {
      return this.getModuleConfig("event.checkin");
    },
    showManualCheckinButton() {
      // Show manual checkin button when user hasn't registered and form has required fields
      return this.content === '您尚未報名' && this.canDoManualCheckin;
    },
    canDoManualCheckin() {
      // Check if form has both name and phone fields with proper assign_to_customer_data and manual checkin is enabled
      if (!this.form || !this.form.subjects) return false;

      const hasNameField = this.form.subjects.some(subject =>
        subject.config?.assign_to_customer_data === 'extra.name'
      );
      const hasPhoneField = this.form.subjects.some(subject =>
        subject.config?.assign_to_customer_data === 'mobile_phone'
      );
      const isManualCheckinEnabled = this.form.config?.event_setting?.enable_manual_checkin ?? false;

      return hasNameField && hasPhoneField && isManualCheckinEnabled;
    }
  },
};
</script>

<style lang="scss" scoped>
</style>
